import { Box, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import { CLAIMS } from '@/north-star/NorthStar.content';
import {
  APPLICATIONS,
  type Application,
  type ApplicationStatus,
} from '@/north-star/NorthStar.fixtures';

// Claims tab (the shell owns the status bar and nav): one list of everything on
// the go for the member, each row showing its annual value and where the claim
// is up to. A claim that's "Ready to submit" is tappable — it opens the review.

// Status pill tones, from theme tokens (no hardcoded colours).
const STATUS_TONE: Record<ApplicationStatus, { bg: string; color: string }> = {
  'Not started': { bg: 'custom.neutral.100', color: 'text.secondary' },
  'In progress': { bg: 'sunflower.main', color: 'custom.charcoal' },
  'Ready to submit': { bg: 'custom.coral.50', color: 'custom.coral.700' },
  Submitted: { bg: 'custom.marineGreen.50', color: 'custom.charcoal' },
  Awarded: { bg: 'custom.limeGreen.100', color: 'custom.limeGreen.800' },
};

export type NorthStarClaimsProps = {
  /** Open the review for a claim that's ready to submit. */
  onReviewClaim: (claim: Application) => void;
};

const ClaimRow = ({
  app,
  onReview,
}: {
  app: Application;
  onReview: () => void;
}) => {
  const tone = STATUS_TONE[app.status];
  const reviewable = app.status === 'Ready to submit';
  return (
    <Stack
      // Only the ready-to-submit claim is interactive — it opens the review.
      component={reviewable ? 'button' : 'div'}
      onClick={reviewable ? onReview : undefined}
      direction='row'
      spacing={2}
      sx={[
        {
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          width: '100%',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'custom.neutral.200',
          borderRadius: `${radius.lg}px`,
        },
        reviewable ? { cursor: 'pointer', textAlign: 'left' } : {},
      ]}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='subtitle2'>{app.name}</Typography>
        <Box
          sx={{
            display: 'inline-flex',
            mt: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: `${radius.xxl}px`,
            bgcolor: tone.bg,
          }}
        >
          <Typography variant='caption' color={tone.color}>
            {app.status}
          </Typography>
        </Box>
      </Box>
      <Stack
        sx={{ alignItems: 'center', flexShrink: 0 }}
        direction='row'
        spacing={1}
      >
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant='subtitle1' color='custom.coral.700'>
            {app.annual}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            a year
          </Typography>
        </Box>
        {reviewable && (
          <Box sx={{ display: 'flex', color: 'custom.coral.700' }}>
            <Icon icon={'chevron-right' as never} fontSize={14} />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

const NorthStarClaims = ({ onReviewClaim }: NorthStarClaimsProps) => (
  <Stack spacing={2}>
    <Typography variant='h2'>{CLAIMS.title}</Typography>
    <Stack spacing={1.5}>
      {APPLICATIONS.map((a) => (
        <ClaimRow key={a.name} app={a} onReview={() => onReviewClaim(a)} />
      ))}
    </Stack>
  </Stack>
);

NorthStarClaims.displayName = 'NorthStarClaims';
export default NorthStarClaims;
